import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

export function ExtensionsPage(): Node {
  return html`
    <div>
      <h1>Extensions</h1>
      <p><code>registerDirective</code> and <code>setEffectHook</code> allow you to extend onefold's template engine and effect system.</p>

      <h2>registerDirective</h2>
      <p>Add custom behavior to elements via attribute directives:</p>
      ${CodeBlock(`import { registerDirective } from 'onefold/extend';

// Register a tooltip directive
registerDirective('tooltip', (element, value) => {
  element.setAttribute('title', value);
  element.style.cursor = 'help';
});

// Usage in templates
html\`<span tooltip="More info here">Hover me</span>\``)}

      <h2>More Directive Examples</h2>
      ${CodeBlock(`// Click-outside directive
registerDirective('click-outside', (element, handler) => {
  const listener = (e: Event) => {
    if (!element.contains(e.target as Node)) {
      handler();
    }
  };
  document.addEventListener('click', listener);
  // Return cleanup function
  return () => document.removeEventListener('click', listener);
});

// Auto-focus directive
registerDirective('autofocus', (element) => {
  requestAnimationFrame(() => (element as HTMLElement).focus());
});

// Intersection observer directive
registerDirective('visible', (element, callback) => {
  const observer = new IntersectionObserver(([entry]) => {
    if (entry.isIntersecting) callback();
  });
  observer.observe(element);
  return () => observer.disconnect();
});`)}

      <h2>setEffectHook</h2>
      <p>Intercept or wrap the effect system for logging, profiling, or debugging:</p>
      ${CodeBlock(`import { setEffectHook } from 'onefold/extend';

// Log every effect execution
setEffectHook({
  onRun: (effectId, fn) => {
    console.log(\`Effect \${effectId} running\`);
    const start = performance.now();
    fn();
    console.log(\`Effect \${effectId} took \${performance.now() - start}ms\`);
  },
  onDispose: (effectId) => {
    console.log(\`Effect \${effectId} disposed\`);
  },
});`)}

      ${Callout('Extensions are global. Register them once at app startup, before mounting.')}

      <h2>API</h2>
      <table>
        <tr><th>Function</th><th>Parameters</th><th>Description</th></tr>
        <tr><td><code>registerDirective</code></td><td>(name, handler)</td><td>Register a custom attribute directive.</td></tr>
        <tr><td><code>setEffectHook</code></td><td>(hooks)</td><td>Intercept effect creation and disposal.</td></tr>
      </table>

      <h2>Next Steps</h2>
      <ul>
        <li><a href="/devtools">DevTools</a> — inspect state, effects, and component trees</li>
        <li><a href="/plugins">Plugins</a> — reusable feature packages for onefold apps</li>
      </ul>

      <h2>Try It</h2>
      <p>Register custom directives and see them applied to elements:</p>

      ${Playground(`import { html, mount } from 'onefold';
import { registerDirective } from 'onefold/extend';

// Register custom directives
registerDirective('tooltip', (el, value) => {
  el.title = String(value);
  el.style.cursor = 'help';
  el.style.borderBottom = '1px dashed #94a3b8';
});

registerDirective('highlight', (el, value) => {
  el.style.backgroundColor = value ? String(value) : '#fef08a';
  el.style.padding = '2px 4px';
  el.style.borderRadius = '3px';
});

registerDirective('uppercase', (el) => {
  el.style.textTransform = 'uppercase';
  el.style.letterSpacing = '0.5px';
  el.style.fontWeight = '600';
});

function App() {
  return html\`
    <div>
      <h3>Custom Directives — registerDirective</h3>
      <p style="font-size:13px;color:#666;margin-bottom:16px">
        Directives add reusable DOM behaviors via d-name attributes.
      </p>

      <div style="display:grid;gap:12px">
        <p>Hover over this: <span d-tooltip="This is a tooltip!">tooltip text</span></p>
        <p>This is <span d-highlight="#bbf7d0">highlighted green</span> and this is <span d-highlight="#bfdbfe">highlighted blue</span></p>
        <p d-uppercase>this text is uppercased via directive</p>
      </div>

      <div style="margin-top:16px;padding:12px;background:#f8fafc;border-radius:8px;font-size:12px;color:#666">
        <p>Directives registered: d-tooltip, d-highlight, d-uppercase</p>
        <p>Usage: just add d-name="value" to any element in your html template.</p>
      </div>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'registerDirective — Custom d-* Attributes')}
    </div>
  `;
}
